/* global edawpData */

import initShowMoreFilters from './utils/archive-filters-show-more';
import initArchiveFiltersMobileToggle from './utils/archive-filters-mobile-toggle';

function datasetBoolean( value, fallback = false ) {
	if ( typeof value === 'undefined' || value === null ) {
		return fallback;
	}

	if ( typeof value === 'boolean' ) {
		return value;
	}

	if ( typeof value === 'string' ) {
		const normalized = value.toLowerCase();
		if ( normalized === 'true' ) {
			return true;
		}
		if ( normalized === 'false' ) {
			return false;
		}
	}

	return fallback;
}

// Initialize variables
const defaultPostsPerPage = 10;

document.addEventListener( 'DOMContentLoaded', function() {
	initRoleButtonKeyboardSupport();
	initArchiveFilters();
} );

/**
 * Ensure elements with role="button" respond to the spacebar key.
 */
const initRoleButtonKeyboardSupport = ( () => {
	let initialized = false;

	return function init() {
		if ( initialized ) {
			return;
		}
		initialized = true;

		document.addEventListener( 'keydown', function( event ) {
			if ( event.defaultPrevented ) {
				return;
			}

			const { key, target } = event;
			const isSpace = key === ' ' || key === 'Spacebar';
			const isEnter = key === 'Enter';

			if ( ! isSpace && ! isEnter ) {
				return;
			}

			if ( ! ( target instanceof Element ) ) {
				return;
			}

			const button = target.closest( '[role="button"]' );

			if ( ! button ) {
				return;
			}

			if ( button.tagName === 'BUTTON' || button.tagName === 'INPUT' ) {
				return;
			}

			if ( button.hasAttribute( 'disabled' ) || button.getAttribute( 'aria-disabled' ) === 'true' ) {
				return;
			}

			event.preventDefault();
			button.click();
		} );
	};
} )();

/**
 * Initialize the archive filters functionality
 */
function initArchiveFilters() {
	const forms = document.querySelectorAll( '.edawp-archive-form[data-ajax-enabled="true"]' );

	forms.forEach( function( form ) {
		initShowMoreFilters( form );
		initArchiveFiltersMobileToggle( form );

		// Handle form submission
		form.addEventListener( 'submit', function( e ) {
			e.preventDefault();
			const page = 1; // Reset to page 1 on new filter
			fetchPosts( page, form );
		} );

		// Handle reset button click
		const resetButton = form.querySelector( '.edawp-archive-form__clear-filters' );
		if ( resetButton ) {
			resetButton.addEventListener( 'click', function( e ) {
				e.preventDefault(); // Prevent any default behavior
				e.stopPropagation(); // Stop event from bubbling up

				// Clear form fields
				const searchField = form.querySelector( 'input[name="edawp_search"]' );
				if ( searchField ) {
					searchField.value = '';
				}

				const checkboxes = form.querySelectorAll( 'input[type="checkbox"]' );
				checkboxes.forEach( function( cb ) {
					cb.checked = false;
				} );

				// Clear URL parameters
				const url = new URL( window.location.href );
				url.searchParams.delete( 'edawp_search' );
				url.searchParams.delete( 'edawp_category' );
				url.searchParams.delete( 'edawp_post_type' );
				url.searchParams.delete( 'post_type_filter' );
				url.searchParams.delete( 'paged' );
				window.history.pushState( {}, '', url );

				// Submit the cleared form via AJAX
				fetchPosts( 1, form );
			} );
		}
	} );

	// Add pagination click handler
	document.addEventListener( 'click', function( e ) {
		const paginationTarget = e.target.closest( '.page-numbers' );
		if ( ! paginationTarget ) {
			return;
		}
		if ( paginationTarget.tagName === 'SPAN' ) {
			return;
		}

		e.preventDefault();

		let page = parseInt( paginationTarget.getAttribute( 'data-page' ) || '', 10 );
		if ( Number.isNaN( page ) || page <= 0 ) {
			page = derivePageFromLink( paginationTarget.getAttribute( 'href' ) );
		}
		if ( Number.isNaN( page ) || page <= 0 ) {
			page = 1;
		}

		// Find the form related to this pagination element
		// Look for the nearest archive block container, then find its form
		const archiveBlock = paginationTarget.closest( '.wp-block-archivewp-archive-loop, .edawp-archive-posts' );
		let form = null;

		if ( archiveBlock ) {
			// Look for the form in the same parent container as the archive block
			const container = archiveBlock.parentElement;
			if ( container ) {
				form = container.querySelector( '.edawp-archive-form[data-ajax-enabled="true"]' );
			}
		}

		// Fallback: try to find form by traversing up from pagination
		if ( ! form ) {
			form = paginationTarget.closest( '.edawp-archive-form' );
		}

		// Final fallback: look for any nearby form (within same block context)
		if ( ! form ) {
			const wrapper = paginationTarget.closest( '[class*="wp-block"]' );
			if ( wrapper ) {
				form = wrapper.querySelector( '.edawp-archive-form[data-ajax-enabled="true"]' );
			}
		}

		// Attempt to locate the form via the pagination/list association.
		if ( ! form ) {
			const paginationContainer = paginationTarget.closest( '.edawp-pagination' );
			const paginationId = paginationContainer ? paginationContainer.id : '';

			if ( paginationId ) {
				const relatedList = document.querySelector( `.edawp-archive-posts[data-pagination-id="${ paginationId }"]` );

				if ( relatedList ) {
					let ancestor = relatedList.parentElement;

					while ( ancestor && ! form ) {
						form = ancestor.querySelector( '.edawp-archive-form[data-ajax-enabled="true"]' );
						ancestor = ancestor.parentElement;
					}
				}
			}
		}

		// Last resort: fall back to the first AJAX-enabled archive form on the page.
		if ( ! form ) {
			form = document.querySelector( '.edawp-archive-form[data-ajax-enabled="true"]' );
		}

		if ( form ) {
			fetchPosts( page, form );
		}
	} );
}

/**
 * Fetch and display posts
 *
 * @param {number}  page Current page number
 * @param {Element} form The form element
 */
async function fetchPosts( page = 1, form ) {
	// Show loading indicator
	const loadingSpinner = form.querySelector( '.edawp-loading-spinner' );
	if ( loadingSpinner ) {
		loadingSpinner.style.display = 'block';
	}

	// Store the form ID to help find related results container
	const formId = form.id || '';
	const resultsContainer = findResultsContainer( formId );

	// Get form data
	const formData = new FormData( form );
	formData.append( 'action', 'edawp_filter_archives' );
	formData.append( 'nonce', formData.get( 'edawp_filter_nonce' ) || '' );
	formData.append( 'page', page );
	// Derive postsPerPage from associated block attribute, form data, or fallback
	let postsPerPageFromBlock = defaultPostsPerPage;
	let loopBlock = null;
	const formWrapper = form.closest( '[class*="wp-block-"]' );
	if ( formWrapper && formWrapper.parentElement ) {
		loopBlock = formWrapper.parentElement.querySelector( '.wp-block-archivewp-archive-loop[data-posts-per-page]' );
		if ( loopBlock && loopBlock.dataset.postsPerPage ) {
			postsPerPageFromBlock = parseInt( loopBlock.dataset.postsPerPage, 10 ) || defaultPostsPerPage;
		}
	}

	const formPostsPerPage = parseInt( formData.get( 'posts_per_page' ), 10 );
	const postsPerPage = Number.isNaN( formPostsPerPage )
		? parseInt( form.dataset.postsPerPage || postsPerPageFromBlock, 10 )
		: formPostsPerPage;
	formData.set( 'posts_per_page', postsPerPage );

	// Determine whether featured images should be included.
	let showFeaturedImage = false;
	let featuredImageSize = 'thumbnail';

	if ( resultsContainer ) {
		if ( typeof resultsContainer.dataset.showFeaturedImage !== 'undefined' ) {
			showFeaturedImage = resultsContainer.dataset.showFeaturedImage === 'true';
		}
		if ( resultsContainer.dataset.featuredImageSize ) {
			featuredImageSize = resultsContainer.dataset.featuredImageSize;
		}
	}

	if ( loopBlock ) {
		if ( typeof loopBlock.dataset.showFeaturedImage !== 'undefined' ) {
			showFeaturedImage = loopBlock.dataset.showFeaturedImage === 'true';
		}
		if ( loopBlock.dataset.featuredImageSize ) {
			featuredImageSize = loopBlock.dataset.featuredImageSize;
		}
	}

	formData.set( 'post_show_image', showFeaturedImage ? 'true' : 'false' );
	formData.set( 'post_image_size', featuredImageSize );

	let showCategories = true;

	if ( resultsContainer ) {
		if ( typeof resultsContainer.dataset.showCategories !== 'undefined' ) {
			showCategories = resultsContainer.dataset.showCategories === 'true';
		}
	}

	if ( loopBlock ) {
		if ( typeof loopBlock.dataset.showCategories !== 'undefined' ) {
			showCategories = loopBlock.dataset.showCategories === 'true';
		}
	}

	formData.set( 'post_show_category', showCategories ? 'true' : 'false' );

	let showReadMore = true;
	let readMoreTextValue = '';
	let readMoreSrTextValue = '';

	if ( resultsContainer ) {
		if ( typeof resultsContainer.dataset.showReadMore !== 'undefined' ) {
			showReadMore = resultsContainer.dataset.showReadMore === 'true';
		}
		if ( resultsContainer.dataset.readMoreText ) {
			readMoreTextValue = resultsContainer.dataset.readMoreText;
		}
		if ( resultsContainer.dataset.readMoreSrText ) {
			readMoreSrTextValue = resultsContainer.dataset.readMoreSrText;
		}
	}

	if ( loopBlock ) {
		if ( typeof loopBlock.dataset.showReadMore !== 'undefined' ) {
			showReadMore = loopBlock.dataset.showReadMore === 'true';
		}
		if ( loopBlock.dataset.readMoreText ) {
			readMoreTextValue = loopBlock.dataset.readMoreText;
		}
		if ( loopBlock.dataset.readMoreSrText ) {
			readMoreSrTextValue = loopBlock.dataset.readMoreSrText;
		}
	}

	const localizedReadMore = edawpData && edawpData.i18n && edawpData.i18n.readMore ? edawpData.i18n.readMore : '';
	if ( ! readMoreTextValue && localizedReadMore ) {
		readMoreTextValue = localizedReadMore;
	}

	const localizedReadMoreSr = edawpData && edawpData.i18n && edawpData.i18n.readMoreSrText ? edawpData.i18n.readMoreSrText : '';
	if ( ! readMoreSrTextValue && localizedReadMoreSr ) {
		readMoreSrTextValue = localizedReadMoreSr;
	}

	formData.set( 'post_show_read_more_link', showReadMore ? 'true' : 'false' );

	if ( readMoreTextValue ) {
		formData.set( 'post_read_more_text', readMoreTextValue );
	}

	if ( readMoreSrTextValue ) {
		formData.set( 'post_read_more_sr_text', readMoreSrTextValue );
	}

	if ( resultsContainer ) {
		const { dataset } = resultsContainer;

		if ( resultsContainer.id ) {
			formData.set( 'container_id', resultsContainer.id );
		}

		if ( dataset.postHeadingTag ) {
			formData.set( 'post_heading_tag', dataset.postHeadingTag );
		}

		if ( typeof dataset.showPublishedDate !== 'undefined' ) {
			formData.set( 'post_show_published_date', datasetBoolean( dataset.showPublishedDate, true ) ? 'true' : 'false' );
		}

		if ( typeof dataset.showArchivedDate !== 'undefined' ) {
			formData.set( 'post_show_archived_date', datasetBoolean( dataset.showArchivedDate, true ) ? 'true' : 'false' );
		}

		if ( typeof dataset.showArchiveDate !== 'undefined' ) {
			formData.set( 'post_show_archive_date', datasetBoolean( dataset.showArchiveDate, true ) ? 'true' : 'false' );
		}

		if ( typeof dataset.showExcerpt !== 'undefined' ) {
			formData.set( 'post_show_excerpt', datasetBoolean( dataset.showExcerpt, true ) ? 'true' : 'false' );
		}

		if ( typeof dataset.showTaxonomies !== 'undefined' ) {
			formData.set( 'post_show_taxonomies', datasetBoolean( dataset.showTaxonomies, true ) ? 'true' : 'false' );
		}

		if ( dataset.excerptWordCount ) {
			formData.set( 'post_excerpt_word_count', dataset.excerptWordCount );
		}

		if ( dataset.noPostsMessage ) {
			formData.set( 'no_posts_message', dataset.noPostsMessage );
		}

		if ( dataset.postWrapperTag ) {
			formData.set( 'post_wrapper_tag', dataset.postWrapperTag );
		}

		if ( dataset.postWrapperClass ) {
			formData.set( 'post_wrapper_class', dataset.postWrapperClass );
		}

		if ( typeof dataset.paginationEnabled !== 'undefined' ) {
			formData.set( 'pagination_show', datasetBoolean( dataset.paginationEnabled, true ) ? 'true' : 'false' );
		}

		if ( dataset.paginationId ) {
			formData.set( 'pagination_id', dataset.paginationId );
		}

		if ( dataset.paginationPrevText ) {
			formData.set( 'pagination_prev_text', dataset.paginationPrevText );
		}

		if ( dataset.paginationNextText ) {
			formData.set( 'pagination_next_text', dataset.paginationNextText );
		}
	}

	if ( loopBlock ) {
		if ( ! formData.has( 'post_heading_tag' ) && loopBlock.dataset.postHeadingTag ) {
			formData.set( 'post_heading_tag', loopBlock.dataset.postHeadingTag );
		}

		if ( ! formData.has( 'post_show_published_date' ) && typeof loopBlock.dataset.showPublishedDate !== 'undefined' ) {
			formData.set( 'post_show_published_date', datasetBoolean( loopBlock.dataset.showPublishedDate, true ) ? 'true' : 'false' );
		}

		if ( ! formData.has( 'post_show_archived_date' ) && typeof loopBlock.dataset.showArchivedDate !== 'undefined' ) {
			formData.set( 'post_show_archived_date', datasetBoolean( loopBlock.dataset.showArchivedDate, true ) ? 'true' : 'false' );
		}

		if ( ! formData.has( 'post_show_archive_date' ) && typeof loopBlock.dataset.showArchiveDate !== 'undefined' ) {
			formData.set( 'post_show_archive_date', datasetBoolean( loopBlock.dataset.showArchiveDate, true ) ? 'true' : 'false' );
		}

		if ( ! formData.has( 'post_show_excerpt' ) && typeof loopBlock.dataset.showExcerpt !== 'undefined' ) {
			formData.set( 'post_show_excerpt', datasetBoolean( loopBlock.dataset.showExcerpt, true ) ? 'true' : 'false' );
		}

		if ( ! formData.has( 'post_show_taxonomies' ) && typeof loopBlock.dataset.showTaxonomies !== 'undefined' ) {
			formData.set( 'post_show_taxonomies', datasetBoolean( loopBlock.dataset.showTaxonomies, true ) ? 'true' : 'false' );
		}

		if ( ! formData.has( 'post_excerpt_word_count' ) && loopBlock.dataset.excerptWordCount ) {
			formData.set( 'post_excerpt_word_count', loopBlock.dataset.excerptWordCount );
		}

		if ( ! formData.has( 'pagination_show' ) && typeof loopBlock.dataset.showPagination !== 'undefined' ) {
			formData.set( 'pagination_show', datasetBoolean( loopBlock.dataset.showPagination, true ) ? 'true' : 'false' );
		}

		if ( ! formData.has( 'pagination_prev_text' ) && loopBlock.dataset.paginationPrevText ) {
			formData.set( 'pagination_prev_text', loopBlock.dataset.paginationPrevText );
		}

		if ( ! formData.has( 'pagination_next_text' ) && loopBlock.dataset.paginationNextText ) {
			formData.set( 'pagination_next_text', loopBlock.dataset.paginationNextText );
		}
	}

	// Get search and categories for URL updates
	const searchKeyword = formData.get( 'edawp_search' ) || '';
	const categories = formData.getAll( 'category[]' );

	try {
		// Make the AJAX request
		const response = await fetch( edawpData.ajaxUrl, {
			method: 'POST',
			body: formData,
		} );

		const data = await response.json();

		if ( data.success && data.data ) {
			applyServerMarkup( data.data.html, data.data, formId );
			updateResultsCount( data.data.found_posts, formId );

			// Update URL with query parameters for browser history
			const urlParams = new URLSearchParams( window.location.search );

			// Set or remove search parameter
			if ( searchKeyword ) {
				urlParams.set( 'edawp_search', searchKeyword );
			} else {
				urlParams.delete( 'edawp_search' );
			}

			// Set or remove category parameter
			if ( categories.length > 0 ) {
				urlParams.set( 'edawp_category', categories.join( ',' ) );
			} else {
				urlParams.delete( 'edawp_category' );
			}

			// Set or remove post type filter parameter
			const postTypeFilters = Array.from( form.querySelectorAll( 'input[name="post_type_filter[]"]:checked' ) ).map( ( cb ) => cb.value );
			if ( postTypeFilters.length > 0 ) {
				urlParams.set( 'edawp_post_type', postTypeFilters.join( ',' ) );
			} else {
				urlParams.delete( 'edawp_post_type' );
			}

			// Set or remove page parameter
			if ( page > 1 ) {
				urlParams.set( 'paged', page );
			} else {
				urlParams.delete( 'paged' );
			}

			// Build the new URL, decoding commas in the category parameter for readability.
			const newQueryString = urlParams.toString().replace( /%2C/g, ',' );
			const newUrl = window.location.pathname + ( newQueryString ? '?' + newQueryString : '' );
			window.history.pushState( { page }, '', newUrl );
		} else {
			// eslint-disable-next-line no-console
			console.error( 'Error in AJAX response:', data );
		}
	} catch ( error ) {
		// eslint-disable-next-line no-console
		console.error( 'Error fetching posts:', error );
	} finally {
		// Hide loading indicator
		if ( loadingSpinner ) {
			loadingSpinner.style.display = 'none';
		}
	}
}

/**
 * Locate the archive results container related to a form submission.
 *
 * @param {string} formId Optional form ID to help find the related results container.
 * @return {HTMLElement|null} Results container element or null if not found.
 */
function findResultsContainer( formId = '' ) {
	// Find the archive results container
	// This could be the default ID or one of the dynamic IDs used by blocks or shortcodes
	let resultsContainer = null;

	// Try to find results container based on form ID first (most specific)
	if ( formId ) {
		// Extract any unique identifiers from the form ID
		const formIdParts = formId.match( /-([a-z0-9-]+)$/ );
		if ( formIdParts && formIdParts[ 1 ] ) {
			const uniquePart = formIdParts[ 1 ];
			// Try to find results container with same unique part
			resultsContainer = document.getElementById( 'edawp-archive-results-' + uniquePart );
		}
	}

	// If not found by form ID, try the default ID
	if ( ! resultsContainer ) {
		resultsContainer = document.getElementById( 'edawp-archive-results' );
	}

	// If still not found, look for any containers with IDs that start with 'edawp-archive-results'
	if ( ! resultsContainer ) {
		const possibleContainers = document.querySelectorAll( '[id^="edawp-archive-results"]' );
		if ( possibleContainers.length > 0 ) {
			resultsContainer = possibleContainers[ 0 ]; // Use the first one found
		}
	}

	if ( ! resultsContainer ) {
		// eslint-disable-next-line no-console
		console.error( 'Archive results container not found' );
		return null;
	}

	return resultsContainer;
}

/**
 * Replace archive markup with server-rendered HTML.
 *
 * @param {string} html   Rendered HTML returned from the server.
 * @param {Object} meta   Metadata returned with the AJAX response.
 * @param {string} formId Optional form ID for scoping container lookup.
 */
function applyServerMarkup( html = '', meta = {}, formId = '' ) {
	const existingContainer = findResultsContainer( formId );
	if ( ! existingContainer ) {
		return;
	}

	const parser = new DOMParser();
	const doc = parser.parseFromString( html || '', 'text/html' );

	const targetContainerId = meta.container_id || existingContainer.id;
	const newContainer = targetContainerId ? doc.getElementById( targetContainerId ) : doc.querySelector( '.edawp-archive-posts' );

	if ( ! newContainer ) {
		existingContainer.innerHTML = '';
		return;
	}

	// Find and handle the heading BEFORE replacing the container.
	const newHeading = doc.querySelector( 'h1.screen-reader-text, h2.screen-reader-text, h3.screen-reader-text, h4.screen-reader-text, h5.screen-reader-text, h6.screen-reader-text' );
	let currentHeading = null;

	// Search for existing heading before the container
	let sibling = existingContainer.previousElementSibling;
	while ( sibling ) {
		if ( sibling.tagName && /^H[1-6]$/.test( sibling.tagName ) && sibling.classList && sibling.classList.contains( 'screen-reader-text' ) ) {
			currentHeading = sibling;
			break;
		}
		sibling = sibling.previousElementSibling;
	}

	// Replace or insert heading before replacing the container
	if ( newHeading ) {
		if ( currentHeading ) {
			currentHeading.replaceWith( newHeading );
		} else {
			existingContainer.insertAdjacentElement( 'beforebegin', newHeading );
		}
	}

	// Get pagination IDs before replacing the container
	const paginationId = meta.pagination_id || existingContainer.dataset.paginationId || '';
	const derivePaginationInfoId = ( id ) => {
		if ( ! id ) {
			return '';
		}
		if ( id === 'edawp-pagination' ) {
			return 'edawp-pagination__info';
		}
		return id.slice( -5 ) === '-info' ? id : `${ id }-info`;
	};
	const paginationInfoId = meta.pagination_info_id || derivePaginationInfoId( paginationId );

	const newPaginationInfo = paginationInfoId ? doc.getElementById( paginationInfoId ) : null;
	const newPagination = paginationId ? doc.getElementById( paginationId ) : null;

	const currentPaginationInfo = paginationInfoId ? document.getElementById( paginationInfoId ) : null;
	const currentPagination = paginationId ? document.getElementById( paginationId ) : null;

	// Handle pagination info BEFORE replacing the container
	if ( currentPaginationInfo && ! newPaginationInfo ) {
		currentPaginationInfo.remove();
	} else if ( newPaginationInfo ) {
		if ( currentPaginationInfo ) {
			currentPaginationInfo.replaceWith( newPaginationInfo );
		} else {
			// Insert before the container if it doesn't exist
			existingContainer.insertAdjacentElement( 'beforebegin', newPaginationInfo );
		}
	}

	// Now replace the container
	existingContainer.replaceWith( newContainer );

	// Handle the alert region based on whether posts exist
	( function handleAlertRegion() {
		// Try to find an alert region inside the new container, fall back to global id
		let alertDiv = newContainer.querySelector( '.edawp-archive-alert' );
		if ( ! alertDiv ) {
			alertDiv = document.getElementById( 'edawp-archive-alert' );
		}

		if ( ! alertDiv ) {
			return;
		}

		// Helper to safely insert a paragraph and force screen-reader re-announcement.
		const insertNoResultsMessage = function( message ) {
			// Ensure ARIA attributes are present for a live region
			alertDiv.setAttribute( 'role', 'alert' );
			alertDiv.setAttribute( 'aria-live', 'polite' );
			alertDiv.setAttribute( 'aria-atomic', 'true' );

			// Create the paragraph with class and text content.
			const p = document.createElement( 'p' );
			p.className = 'edawp-no-results';
			p.textContent = message || '';

			// To ensure the message is re-announced if it's identical to the previous one,
			// clear the region and re-add the content on the next animation frame.
			// Remove children first (keeps attributes intact).
			while ( alertDiv.firstChild ) {
				alertDiv.removeChild( alertDiv.firstChild );
			}

			// Use requestAnimationFrame to allow assistive tech to notice the removal before adding.
			window.requestAnimationFrame( function() {
				// A tiny timeout after rAF gives some additional spacing in some browsers/AT.
				setTimeout( function() {
					alertDiv.appendChild( p );
				}, 20 );
			} );
		};

		// Determine whether the returned markup contains posts (a UL is used for posts).
		const hasPosts = !! newContainer.querySelector( 'ul' );

		// Resolve message from AJAX meta first, then from dataset, then a default string.
		const serverMessage = meta && ( meta.no_posts_message || meta.no_posts_message_text || meta.no_posts_found ) ? ( meta.no_posts_message || meta.no_posts_message_text || meta.no_posts_found ) : null;
		const datasetMessage = newContainer && newContainer.dataset && newContainer.dataset.noPostsMessage ? newContainer.dataset.noPostsMessage : '';
		const defaultMessage = 'No archived content found matching your criteria.';
		const message = serverMessage || datasetMessage || defaultMessage;

		if ( ! hasPosts ) {
			// No posts: insert paragraph into the live region
			insertNoResultsMessage( message );
		} else {
			// Posts found: clear the live region
			while ( alertDiv.firstChild ) {
				alertDiv.removeChild( alertDiv.firstChild );
			}
		}
	}() );

	// Handle pagination controls after replacing the container
	if ( currentPagination && ! newPagination ) {
		currentPagination.remove();
	} else if ( newPagination ) {
		if ( currentPagination ) {
			currentPagination.replaceWith( newPagination );
		} else {
			newContainer.insertAdjacentElement( 'afterend', newPagination );
		}
	}
}

/**
 * Try to locate the results count element associated with the archive list.
 *
 * @param {string} formId Optional form ID to help scope the lookup.
 * @return {HTMLElement|null} Results count element if found.
 */
function findResultsCountElement( formId = '' ) {
	const resultsContainer = findResultsContainer( formId );
	if ( ! resultsContainer ) {
		return null;
	}

	let resultsCount = null;
	const parent = resultsContainer.parentNode;

	if ( parent ) {
		resultsCount = parent.querySelector( '.edawp-results-count' );
		if ( ! resultsCount && parent.parentNode ) {
			resultsCount = parent.parentNode.querySelector( '.edawp-results-count' );
		}
	}

	if ( ! resultsCount ) {
		resultsCount = document.querySelector( '.edawp-results-count' );
	}

	return resultsCount;
}

/**
 * Update the visible results count if an element exists.
 *
 * @param {number} totalFound Number of posts found.
 * @param {string} formId     Optional form ID to assist targeting.
 */
function updateResultsCount( totalFound, formId = '' ) {
	const countEl = findResultsCountElement( formId );
	if ( ! countEl || ! edawpData || ! edawpData.i18n ) {
		return;
	}

	const found = parseInt( totalFound, 10 );
	const safeTotal = Number.isNaN( found ) ? 0 : found;
	const template = safeTotal === 1 ? edawpData.i18n.foundResult : edawpData.i18n.foundResults;
	countEl.textContent = template.replace( '%d', safeTotal );
}

/**
 * Derive the page number from a pagination link href.
 *
 * @param {string} href Anchor href attribute.
 * @return {number} Page number.
 */
function derivePageFromLink( href ) {
	if ( ! href ) {
		return 1;
	}

	try {
		const url = new URL( href, window.location.origin );
		const paged = url.searchParams.get( 'paged' );
		if ( paged ) {
			const parsed = parseInt( paged, 10 );
			if ( ! Number.isNaN( parsed ) && parsed > 0 ) {
				return parsed;
			}
		}

		const match = url.pathname.match( /\/page\/(\d+)/ );
		if ( match && match[ 1 ] ) {
			const parsed = parseInt( match[ 1 ], 10 );
			if ( ! Number.isNaN( parsed ) && parsed > 0 ) {
				return parsed;
			}
		}
	} catch ( error ) {
		// Ignore parsing errors.
	}

	return 1;
}
